<template>
    <page-shell :title="experienceTitle">
        <div v-if="!loading" class="experience">
            <div class="detail-actions">
                <font-awesome-icon @click="refreshData" icon="fa fa-rotate-right"
                    class="resize-navbar-icon" />
            </div>
            <x-cubes-filter v-model="filter" :locations="locations"></x-cubes-filter>

            <el-table :data="tableData" height="calc(100vh - 500px)" width="100%">
                <el-table-column prop="fullName" label="Name" sortable></el-table-column>
                <el-table-column label="Status" width="250">
                    <template slot-scope="scope">
                        <el-button-group>
                            <el-button :type="scope.row.status === 'on' ? 'success' : 'default'" @click="changeStatus(scope.row, 'on')">
                                <span>on</span>
                            </el-button>
                            <el-button :type="scope.row.status === 'demo' ? 'primary' : 'default'" @click="changeStatus(scope.row, 'demo')">
                                <span>demo</span>
                            </el-button>
                            <el-button :type="scope.row.status === 'off' ? 'danger' : 'default'" @click="changeStatus(scope.row, 'off')">
                                <span>off</span>
                            </el-button>
                        </el-button-group>
                    </template>
                </el-table-column>
            </el-table>
        </div>
        <spinner :loading="loading" text="Loading applications..."></spinner>
    </page-shell>
</template>

<script setup>
import { computed, onMounted, ref, watch } from "vue";
import { useRoute } from "vue-router/composables";
import PageShell from "../shared/PageShell.vue";
import Spinner from "../shared/Spinner.vue";
import XCubesFilter from "../XCubesFilter.vue";
import { createXCubeExperienceStatus, getExperienceSimple, getXCubeExperiences, updateXCubeExperienceStatus } from "../../api/graphql/experiences";
import { getUserLocationsSimple } from "../../api/graphql/locations";
import { getXCubesSimple } from "../../api/graphql/xcubes";
import { Message } from "element-ui";


const ALL_OPTION = {
    name: "All",
    id: "all",
    display: "All",
};

const filter = ref({
    active: { name: "Active", id: "active" },
    location: ALL_OPTION,
    type: { name: "XCube", id: "xcube" },
    country: ALL_OPTION,
});

const route = useRoute();
const experience = ref(null);
const experienceTitle = computed(() => (
    experience.value
        ? `${experience.value.name} - ${experience.value.code}`
        : "Experience"
));
const xCubeExperiences = ref([]);
const allXCubes = ref([]);
const loading = ref(true);
const locations = ref([]);


const refreshData = async () => {
    loading.value = true;
    xCubeExperiences.value = await getXCubeExperiences(route.params.id);
    experience.value = await getExperienceSimple(route.params.id);
    allXCubes.value = await getFilteredDevices();
    loading.value = false;
};

const tableData = computed(() => {
    if (!allXCubes.value || !xCubeExperiences.value) {
        return [];
    }
    return allXCubes.value.map((xcube) => {
        const xCubeExperience = xCubeExperiences.value.find(
            (xCubeExperience) => xCubeExperience.xcube?.id === xcube.id
        );
        return {
            ...xcube,
            fullName: `${xcube.location.name} - ${xcube.name}`,
            xCubeExperienceId: xCubeExperience ? xCubeExperience.id : null,
            status: xCubeExperience ? xCubeExperience.status : "off",
            url: xCubeExperience ? xCubeExperience.public_booking_url : "",
        };
    });
});

const cleanedFilter = computed(() => {
    const filterValue = {
    };
    if (filter.value.active?.id && filter.value.active.id !== ALL_OPTION.id) {
        filterValue.status = filter.value.active.id;
    }
    if (filter.value.location?.id && filter.value.location.id !== ALL_OPTION.id) {
        filterValue.location = filter.value.location.id;
    }
    if (filter.value.type?.id && filter.value.type.id !== ALL_OPTION.id) {
        filterValue.type = filter.value.type.id;
    }
    if (filter.value.country?.id && filter.value.country.id !== ALL_OPTION.id) {
        filterValue.country = filter.value.country.id;
    }
    return filterValue;
});

const changeStatus = async (row, newStatus) => {
    // Implement status change logic here
    if (row.xCubeExperienceId) {
        console.log("Updating status association", row);
        await updateXCubeExperienceStatus(row.xCubeExperienceId, newStatus);
    } else {
        console.log("Creating new status association", row);
        await createXCubeExperienceStatus(row.id, experience.value.id, newStatus);
        // Create new experience association with status
        // await createXCubeExperience(row.id, experience.value.id, newStatus);
    }
    console.log("Refreshing data after status change", route.params.id);
    xCubeExperiences.value = await getXCubeExperiences(route.params.id);
    Message.success(`Status for ${row.fullName} changed to ${newStatus}`);
};


const getFilteredDevices = async () => {
    return await getXCubesSimple(cleanedFilter.value);
};

onMounted(async () => {
    loading.value = true;
    const result = await Promise.all([
        getExperienceSimple(route.params.id),
        getFilteredDevices(),
        getXCubeExperiences(route.params.id),
        getUserLocationsSimple(),
    ]);
    [experience.value, allXCubes.value, xCubeExperiences.value, locations.value] = result;
    loading.value = false;
});

watch(
    () => filter,
    async () => {
        loading.value = true;
        allXCubes.value = await getFilteredDevices();
        loading.value = false;
    },
    { deep: true }
);

</script>

<style scoped lang="scss">
.experience {
    width: 100%;
    min-width: 0;
}

.detail-actions {
    margin-bottom: 0.75rem;
}

.resize-navbar-icon {
    cursor: pointer;
}
</style>